import { useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../../../services/api';

/**
 * Custom hook for managing user CRUD operations
 * Handles all user-related API calls and state management
 */
export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);

  /**
   * Fetch users with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Current page number
   * @param {string} params.selectedRole - Filter by role
   * @param {string} params.selectedStatus - Filter by status
   * @param {string} params.debouncedSearch - Search query
   */
  const fetchUsers = async ({
    page = 1,
    selectedRole = '',
    selectedStatus = '',
    debouncedSearch = ''
  } = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });

      if (selectedRole) params.append('role', selectedRole);
      if (selectedStatus) params.append('status', selectedStatus);
      if (debouncedSearch.trim()) params.append('search', debouncedSearch.trim());

      const response = await fetch(`http://localhost:5000/api/admin/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotalPages(data.totalPages);
        return { success: true, data };
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to load users');
        return { success: false, error };
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create a new user
   * @param {Object} userData - User data to create
   */
  const createUser = async (userData) => {
    try {
      const response = await fetch('http://localhost:5000/api/admin/create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${userData.role.charAt(0).toUpperCase() + userData.role.slice(1)} user created successfully!`);
        return { success: true, data };
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to create user');
        return { success: false, error };
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Error creating user');
      return { success: false, error };
    }
  };

  /**
   * Update an existing user
   * @param {string} userId - User ID to update
   * @param {Object} userData - Updated user data
   */
  const updateUser = async (userId, userData) => {
    try {
      // Create a copy of userData and remove password if it's empty
      const updateData = { ...userData };
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password;
      }

      const response = await fetch(`http://localhost:5000/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('User updated successfully!');
        return { success: true, data };
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update user');
        return { success: false, error };
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Error updating user');
      return { success: false, error };
    }
  };

  /**
   * Delete a user
   * @param {string} userId - User ID to delete
   */
  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return { success: false, cancelled: true };
    }

    try {
      const response = await fetch(`http://localhost:5000/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        return { success: true };
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to delete user');
        return { success: false, error };
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
      return { success: false, error };
    }
  };

  /**
   * Update user status
   * @param {string} userId - User ID
   * @param {string} newStatus - New status (ACTIVE, PENDING, INACTIVE, SUSPENDED)
   */
  const updateUserStatus = async (userId, newStatus) => {
    try {
      const response = await fetch(`http://localhost:5000/api/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const statusText = newStatus === 'ACTIVE' ? 'approved' : 'updated';
        toast.success(`User status ${statusText} successfully!`);
        return { success: true };
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update user status');
        return { success: false, error };
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
      return { success: false, error };
    }
  };

  /**
   * Update agent status (activate/deactivate)
   * @param {string} userId - Agent user ID
   * @param {boolean} isActive - Active status
   */
  const updateAgentStatus = async (userId, isActive) => {
    try {
      await api.patch(`/api/admin/agents/${userId}/status`, { isAgentActive: isActive });
      toast.success(`Agent ${isActive ? 'activated' : 'deactivated'}`);
      return { success: true };
    } catch (error) {
      console.error('Error updating agent status:', error);
      toast.error('Failed to update agent status');
      return { success: false, error };
    }
  };

  /**
   * Update agent commission rate
   * @param {string} userId - Agent user ID
   * @param {number} rate - Commission rate percentage
   */
  const updateAgentCommission = async (userId, rate) => {
    try {
      await api.patch(`/api/admin/agents/${userId}/commission`, { commissionRate: rate });
      toast.success('Commission rate updated');
      return { success: true };
    } catch (error) {
      console.error('Error updating commission rate:', error);
      toast.error('Failed to update commission rate');
      return { success: false, error };
    }
  };

  return {
    // State
    users,
    loading,
    totalPages,

    // Functions
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    updateUserStatus,
    updateAgentStatus,
    updateAgentCommission,

    // Direct state setters (for advanced use cases)
    setUsers,
    setLoading,
    setTotalPages
  };
};

export default useUsers;
